import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  useKivunovaTranslation,
  UserRole
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import { fetchGraphQL } from "../../Graphql/utils";
import { listCompanyStoresQuery } from "../../Graphql/querries/companyStoresQuery";

interface ListStoresPayload {
  listStores: {
    code: string;
    stores?: { id: string; name: string }[];
  };
}

function isCompanyAdminRole(role: unknown): boolean {
  return (
    String(role ?? "")
      .trim()
      .toUpperCase() === UserRole.COMPANY_ADMIN
  );
}

export interface CompanyStoreOption {
  id: string;
  name: string;
}

/**
 * Company-admin store picker: loads stores from `listStores` (GraphQL).
 * **Controlled only** — parent owns `selectedStoreId` and passes `onSelectedStoreIdChange`.
 * Parent persists the selection in Redux for **list filters** only: item list uses
 * `productItems.companyAdminItemListStoreIdByCompany`; catalog list uses
 * `products.companyAdminProductListStoreIdByCompany`. Create-product / add-item modals
 * require an explicit store in-form, not this selection.
 */
export interface CompanyAdminInventoryStoreBarProps {
  /** `card` = bordered block with helper text. `inline` = compact select (toolbar). */
  variant?: "card" | "inline";
  selectedStoreId: string | null;
  onSelectedStoreIdChange: (storeId: string | null) => void;
  /** When `variant` is `inline`, overrides the default tooltip (e.g. catalog vs items list). */
  inlineTooltip?: string;
}

const CompanyAdminInventoryStoreBar: React.FC<
  CompanyAdminInventoryStoreBarProps
> = ({
  variant = "card",
  selectedStoreId,
  onSelectedStoreIdChange,
  inlineTooltip
}) => {
  const { setAlert } = useContext(AlertBannerContext);
  const { t } = useKivunovaTranslation();
  const userDetails = useSelector((s: RootState) => s.user.userDetails);
  const [stores, setStores] = useState<CompanyStoreOption[]>([]);

  const role = userDetails?.role;
  const companyId = userDetails?.companyId;

  const loadStores = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await fetchGraphQL<ListStoresPayload>(
        listCompanyStoresQuery,
        { input: { companyId } },
        setAlert
      );
      const block = res?.listStores;
      if (!block || block.code !== API_RESPONSE_CODE.OK) return;
      const list = (block.stores ?? []).map((s) => ({
        id: s.id,
        name: s.name
      }));
      setStores(list);
    } catch {
      /* fetchGraphQL reports via alerts / app logger */
    }
  }, [companyId, setAlert]);

  useEffect(() => {
    if (!isCompanyAdminRole(role) || !companyId) return;
    void loadStores();
  }, [role, companyId, loadStores]);

  useEffect(() => {
    if (!selectedStoreId || stores.length === 0) return;
    if (!stores.some((x) => x.id === selectedStoreId)) {
      onSelectedStoreIdChange(null);
    }
  }, [stores, selectedStoreId, onSelectedStoreIdChange]);

  if (!isCompanyAdminRole(role) || !companyId) {
    return null;
  }

  const storeHintCard = t(
    "inventory-store-context",
    "Inventory is tracked per store. Select which store you are stocking for."
  );
  const storeHintInline = t(
    "inventory-store-filter-items-hint",
    "Filters the product items list below by store. Clear the selection to show items from all stores."
  );

  const labelId =
    variant === "inline"
      ? "inv-store-select-inline-label"
      : "inv-store-select-label";

  const storeLabel = t("store", "Store");
  const selectPlaceholder = t("select-store", "Select store…");

  const selectControl = (
    <FormControl
      size="small"
      sx={{
        minWidth: variant === "inline" ? 200 : 260,
        width: variant === "inline" ? { xs: "100%", sm: "auto" } : undefined
      }}
    >
      <InputLabel id={labelId} shrink>
        {storeLabel}
      </InputLabel>
      <Select
        labelId={labelId}
        label={storeLabel}
        value={selectedStoreId ?? ""}
        displayEmpty
        data-testid="company-admin-inventory-store-select"
        renderValue={(value) => {
          if (value === "" || value == null) {
            return (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                {selectPlaceholder}
              </Typography>
            );
          }
          const row = stores.find((s) => s.id === value);
          return row?.name || String(value);
        }}
        onChange={(e) => {
          const next = e.target.value === "" ? null : String(e.target.value);
          onSelectedStoreIdChange(next);
        }}
      >
        <MenuItem value="">
          <em>{selectPlaceholder}</em>
        </MenuItem>
        {stores.map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name || s.id}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (variant === "inline") {
    return (
      <Tooltip
        title={inlineTooltip ?? storeHintInline}
        placement="top"
        enterDelay={400}
      >
        <span style={{ display: "inline-flex", maxWidth: "100%" }}>
          {selectControl}
        </span>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper"
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {storeHintCard}
      </Typography>
      {selectControl}
    </Box>
  );
};

export default CompanyAdminInventoryStoreBar;
